import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { UserProfile } from '@/types/profile';
import { useAuth } from '@/context/AuthContext';
import { attemptSilentReauth, SessionExpiredError } from '@/lib/session';

interface ProfileContextValue {
  profile: UserProfile | null;
  profileLoading: boolean;
  saveProfile: (p: Partial<UserProfile>) => Promise<void>;
  saveError: string | null;
  clearSaveError: () => void;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
  });
  if (res.status === 401) {
    // The SWA auth cookie lapsed mid-visit. Try to renew it silently.
    attemptSilentReauth();
    throw new SessionExpiredError(path);
  }
  if (!res.ok) throw new Error(`API ${options?.method ?? 'GET'} ${path} → ${res.status}`);
  if (res.status === 204) return undefined as T;
  return res.json();
}

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const clearSaveError = useCallback(() => setSaveError(null), []);

  const useApi = !import.meta.env.DEV;

  useEffect(() => {
    if (authLoading) return;
    if (!user || !useApi) { setProfileLoading(false); return; }

    apiFetch<UserProfile | null>('/api/profile')
      .then(p => setProfile(p))
      .catch(() => {}) // non-fatal
      .finally(() => setProfileLoading(false));
  }, [authLoading, user?.id, useApi]);

  const saveProfile = useCallback(async (data: Partial<UserProfile>) => {
    const merged = { ...(profile ?? {}), ...data } as UserProfile;
    setProfile(merged);
    if (!useApi) return;
    try {
      const saved = await apiFetch<UserProfile>('/api/profile', {
        method: 'POST',
        body: JSON.stringify(merged),
      });
      setProfile(saved);
    } catch (err) {
      console.error('[ProfileContext] save failed', err);
      setSaveError(
        err instanceof SessionExpiredError
          ? 'Your session had expired. Signing you back in — please try again in a moment.'
          : 'Profile failed to save. Please try again.',
      );
    }
  }, [profile, useApi]);

  return (
    <ProfileContext.Provider value={{ profile, profileLoading, saveProfile, saveError, clearSaveError }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within ProfileProvider');
  return ctx;
}
